"use client";

import classNames from "classnames";
import { MediaController, MediaPosterImage } from "media-chrome/react";
import { observer } from "mobx-react-lite";
import React, { HTMLAttributes } from "react";
import { CenteredChrome } from "./components/CenteredChrome";
import { ControlBar } from "./components/ControlBar";
import { VideoElement } from "./components/VideoElement";
import { useVideoPlayerLogic } from "./hooks/useVideoPlayerLogic";
import styles from "./index.module.css";

export interface IVideoPlayerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onTimeUpdate"> {
  src: string;
  loop?: boolean;
  poster?: string;
  muted?: boolean;
  autoPlay?: boolean;
  showControls?: boolean;
  showPlayButton?: boolean;
  showTimeDisplay?: boolean;
  enableHoverPlay?: boolean;
  disableClickPlay?: boolean;
  disableAutoHide?: boolean;
  onTimeUpdate?: (currentTime: number) => void;
  range?: { start: number; end: number };
  maxHeight?: string | number;
  stopOnUnmount?: boolean;
  playButtonStyle?: {
    container: string;
    icon: string;
  };
  /**
   * 是否自适应内容高度，而不是填满父容器
   * 当设置为 true 时，视频会以 contain 方式展示，不会超出容器
   */
  fitContent?: boolean;
}

export interface IVideoPlayerRef {
  play: () => void;
  pause: () => void;
  getVideoElement: () => HTMLVideoElement | null;
}

const VideoPlayerBase = React.forwardRef<IVideoPlayerRef, IVideoPlayerProps>(
  (props, ref) => {
    const {
      className,
      style,
      playButtonStyle = {
        container: "",
        icon: "",
      },
      disableAutoHide = false,
      enableHoverPlay = false,
      poster,
      showControls = true,
      showPlayButton = true,
      fitContent = false,
      maxHeight,
      muted = false,
      autoPlay = false,
      loop = true,
      src,
    } = props;

    const {
      videoRef,
      isLoaded,
      setIsLoaded,
      handleMouseEnter,
      handleMouseLeave,
      handleExpand,
    } = useVideoPlayerLogic(props, ref);

    return (
      <div
        className={classNames(
          className,
          "overflow-hidden w-full h-full relative",
          styles.wrapper
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <MediaController
          style={style}
          autohide={disableAutoHide ? "-1" : "1"}
          className={classNames(
            "bg-transparent w-full h-full flex flex-col relative z-20"
          )}
        >
          {poster && (
            <MediaPosterImage
              src={poster}
              slot="poster"
              className="object-contain"
            />
          )}

          {isLoaded && (
            <CenteredChrome
              playButtonStyle={playButtonStyle}
              enableHoverPlay={enableHoverPlay}
              showPlayButton={showPlayButton}
            />
          )}

          <VideoElement
            src={src}
            videoRef={videoRef}
            isLoaded={isLoaded}
            setIsLoaded={setIsLoaded}
            fitContent={fitContent}
            maxHeight={maxHeight}
            muted={muted}
            autoPlay={autoPlay}
            loop={loop}
            poster={poster}
          />

          {isLoaded && (
            <ControlBar
              showControls={showControls}
              handleExpand={handleExpand}
            />
          )}
        </MediaController>
      </div>
    );
  }
);

VideoPlayerBase.displayName = "VideoPlayer";

const VideoPlayer = observer(VideoPlayerBase);
VideoPlayer.displayName = "VideoPlayer";

export { VideoPlayer };
